import type { TenantStatus } from './chatbot';

export type PlatformUserRole = 'SUPERADMIN' | 'ADMIN' | 'USER' | 'OPERATOR';
export type PlatformUserStatus = 'ACTIVE' | 'INACTIVE';
export type PlatformUserBotAccessMode = 'ALL_TENANT_BOTS' | 'SELECTED_BOTS';
export type TenantFeature =
  | 'chatbot'
  | 'analytics'
  | 'export'
  | 'priority-support'
  | 'custom-integrations'
  | 'whatsapp-integration'
  | 'whatsapp-360dialog'
  | 'whatsapp-twilio'
  | 'whatsapp-waha'
  | 'whatsapp-evohub'
  | 'telegram-integration'
  | 'facebook-integration'
  | 'instagram-integration'
  | 'helpdesk'
  | 'chatwoot-integration'
  | 'contact-agent'
  | 'contact-agent-audience-engine'
  | 'leads'
  | 'sms-integration'
  | 'email-integration';

/** Módulos de administración que se pueden restringir por usuario. */
export const MODULE_TENANT_FEATURES = [
  'chatbot',
  'helpdesk',
  'chatwoot-integration',
  'whatsapp-integration',
  'whatsapp-360dialog',
  'whatsapp-twilio',
  'whatsapp-waha',
  'whatsapp-evohub',
  'telegram-integration',
  'facebook-integration',
  'instagram-integration',
  'contact-agent',
  'leads',
  'sms-integration',
  'email-integration',
] as const satisfies readonly TenantFeature[];

export type ModuleTenantFeature = (typeof MODULE_TENANT_FEATURES)[number];

/** Features retiradas de la UI de tenant; se eliminan al guardar. */
export const REMOVED_TENANT_UI_FEATURES = [
  'whatsapp-integration',
  'priority-support',
  'custom-integrations',
] as const satisfies readonly TenantFeature[];

export interface TenantNoragConfig {
  empresaId?: number;
  apiUrl?: string;
}

export interface TenantConfig {
  maxUsers: number;
  maxBots: number;
  features: TenantFeature[];
  norag?: TenantNoragConfig;
}

export interface PlatformTenant<TDate extends string | Date = string> {
  id: string;
  name: string;
  slug: string | null;
  status: TenantStatus;
  config: TenantConfig;
  openaiApiKeyConfigured?: boolean;
  anthropicApiKeyConfigured?: boolean;
  openrouterApiKeyConfigured?: boolean;
  localLlmConfigured?: boolean;
  createdAt?: TDate;
  updatedAt?: TDate;
  usersCount?: number;
  instancesCount?: number;
  botsCount?: number;
}

export interface CreatePlatformTenantRequest {
  name: string;
  slug?: string;
  status?: TenantStatus;
  config: TenantConfig;
}

export interface UpdatePlatformTenantRequest extends Partial<CreatePlatformTenantRequest> {
  openaiApiKey?: string;
  anthropicApiKey?: string;
  clearOpenaiApiKey?: boolean;
  clearAnthropicApiKey?: boolean;
  openrouterApiKey?: string;
  clearOpenrouterApiKey?: boolean;
}

export interface PlatformUser<TDate extends string | Date = string> {
  id: number;
  email: string;
  role: PlatformUserRole;
  status: PlatformUserStatus;
  tenantId: string | null;
  botAccessMode?: PlatformUserBotAccessMode;
  telegramId?: string | null;
  whatsappPhone?: string | null;
  tenantName?: string | null;
  tenantStatus?: TenantStatus | null;
  tenantFeatures?: TenantFeature[];
  /** Módulos efectivos para este usuario (intersección tenant + usuario). */
  features?: TenantFeature[];
  featuresEnabled?: TenantFeature[];
  createdAt?: TDate;
  updatedAt?: TDate;
}

export interface CreatePlatformUserRequest {
  email: string;
  password: string;
  role: PlatformUserRole;
  tenantId?: string | null;
  status?: PlatformUserStatus;
  telegramId?: string | null;
  whatsappPhone?: string | null;
}

export interface UpdatePlatformUserRequest {
  email?: string;
  role?: PlatformUserRole;
  tenantId?: string | null;
  status?: PlatformUserStatus;
  telegramId?: string | null;
  whatsappPhone?: string | null;
}

export interface PlatformAssignableBot<TDate extends string | Date = string> {
  instanceBotId: string;
  instanceId: string;
  instanceName: string;
  botId: string;
  botName: string;
  isActive: boolean;
  displayOrder: number;
  createdAt?: TDate;
}

export interface PlatformUserBotAccess<TDate extends string | Date = string> {
  userId: number;
  tenantId: string | null;
  mode: PlatformUserBotAccessMode;
  assignedInstanceBotIds: string[];
  assignableBots: PlatformAssignableBot<TDate>[];
}

export interface UpdatePlatformUserBotAccessRequest {
  mode: PlatformUserBotAccessMode;
  instanceBotIds?: string[];
}

export interface PlatformUserFeatureAccess {
  userId: number;
  tenantId: string | null;
  tenantFeatures: TenantFeature[];
  featuresEnabled: TenantFeature[];
  effectiveFeatures: TenantFeature[];
}

export interface UpdatePlatformUserFeatureAccessRequest {
  featuresEnabled: TenantFeature[];
}

export type BrandPresetId = 'setup' | 'microsyst';

export interface PlatformBranding {
  presetId: BrandPresetId;
  brandName: string;
  logoUrl?: string;
}

export interface UpdatePlatformBrandingRequest {
  presetId: BrandPresetId;
  brandName?: string;
  logoUrl?: string;
}


/** Receta "Nuevo Cobrador": deja un agente de cobranzas listo en un solo paso. */
export interface CreateCobradorRequest {
  tenantId: string;
  /** Nombre del cliente, se usa para nombrar instancia y agente. */
  companyName: string;
  /** Si no viene, "Cobranzas <empresa>". */
  agentName?: string;
  /** Texto libre con CBU, alias, links de pago. */
  paymentMethodsText: string;
  /** Conexión de cartera (Setup) ya creada. Para Excel, la cartera se importa antes. */
  sourceConnectionId?: string | null;
}

export type RecipeStepId =
  | 'modules'
  | 'instance'
  | 'bot'
  | 'agent'
  | 'funnel'
  | 'policies'
  | 'team'
  | 'destinations'
  | 'whatsapp';

/** @deprecated usar RecipeStepId */
export type CobradorRecipeStepId = RecipeStepId;

export interface RecipeStep {
  id: RecipeStepId;
  label: string;
  status: 'done' | 'skipped' | 'pending';
  detail?: string;
}

export type CobradorRecipeStep = RecipeStep;

export interface RecipeResult {
  tenantId: string;
  instanceId: string;
  botId: string;
  agentId: string;
  aiReady: boolean;
  steps: RecipeStep[];
}

/** Receta "Nuevo Vendedor": agente de leads con un embudo armado a partir de tres respuestas. */
export interface CreateVendedorRequest {
  tenantId: string;
  companyName: string;
  /** Si no viene, "Ventas <empresa>". */
  agentName?: string;
  /** Qué vende, en pocas palabras. */
  productName: string;
  /** Cómo se lo contaría a un cliente. Cada línea es un punto que el bot puede citar. */
  productSummary: string;
  /** La pregunta que mide el tamaño del interesado. */
  sizeQuestion: string;
  /** Desde cuánto vale la pena pasarlo a ventas. Vacío: todos. */
  sizeMin?: number | null;
}

export type VendedorRecipeResult = RecipeResult;

/** Receta "Nueva Mesa de ayuda": agente que atiende problemas, abre tickets y llama a una persona. */
export interface CreateMesaRequest {
  tenantId: string;
  companyName: string;
  /** Si no viene, "Soporte <empresa>". */
  agentName?: string;
  /** Qué problemas atiende, en palabras del cliente. */
  scope: string;
  /** Usuarios del cliente a los que avisa cuando hace falta una persona. */
  notifyUserIds: number[];
}

export type MesaRecipeResult = RecipeResult;

export type AsistenteTone = 'CERCANO' | 'FORMAL';

/** Receta "Nuevo Asistente": responde consultas con lo que la empresa le cuenta. */
export interface CreateAsistenteRequest {
  tenantId: string;
  companyName: string;
  /** Si no viene, "Asistente <empresa>". */
  agentName?: string;
  /** Lo que tiene que saber: horarios, productos, precios, preguntas frecuentes. */
  knowledge: string;
  tone: AsistenteTone;
}

export type AsistenteRecipeResult = RecipeResult;

export type RecipeAgentKind = 'ASISTENTE' | 'COBRADOR' | 'VENDEDOR' | 'MESA';

/** Un agente del cliente al que el conserje puede derivar. */
export interface RecipeDestinationOption {
  botId: string;
  name: string;
  kind: RecipeAgentKind;
  /** Texto propuesto para el botón del menú (máx. 24). */
  suggestedLabel: string;
}

export interface ConserjeDestinationInput {
  botId: string;
  menuLabel: string;
  menuDescription?: string;
}

/** Receta "Nuevo Conserje": recibe a todos y los deriva al agente que corresponde. */
export interface CreateConserjeRequest {
  tenantId: string;
  companyName: string;
  /** Si no viene, "Recepción <empresa>". */
  agentName?: string;
  welcomeText: string;
  destinations: ConserjeDestinationInput[];
}

export type ConserjeRecipeResult = RecipeResult;

export type CobradorRecipeResult = RecipeResult;
